import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { clearCustomTableFilters } from "@src/features/table_filtering/utils/base_state_handler_utils";
import { Project } from "@src/types";
import { TabItem } from "@src/types/antd_types";
import {
  ResourcePlanFilters,
  ResourcePlanState,
} from "@src/types/resource_plan_types";

const initialState: ResourcePlanState = {
  projectId: "",
  totalDraftRequests: 0,
  isJointVentureProject: false,
  jointVenturePartner: undefined,
  isProjectEnded: false,
  resourcePlanTableState: {
    quickFilter: "",
    filterResult: [],
    customTableFilters: {
      standardRoleName: [],
      status: [],
      isDeclined: undefined,
      isCommentAvailable: undefined,
      jointVenture: undefined,
      actionRequired: undefined,
    },
    columns: [],
    currentTabConfig: null,
    isFilterApplied: false,
    isTableDisabled: false,
  },
};

/**
 * This function is used to check if the filter is applied
 *
 * @param {ResourcePlanState} state - The state of the table.
 * @returns {boolean} true if the filter is applied, false otherwise
 */
function checkIfFilterApplied(state: ResourcePlanState): boolean {
  const { quickFilter, customTableFilters } = state.resourcePlanTableState;
  const customFilters = Object.values(customTableFilters).flat();
  return quickFilter !== "" || customFilters.length > 0;
}

const resourcePlanSlice = createSlice({
  name: "resourcePlanSlice",
  initialState,
  reducers: {
    /**
     * Set to true if the projekt timeline is ended. When the project timeline ended than there
     * will be some information shown to notify the user.
     *
     * @param state The resource plan state with all values.
     * @param action The state if the project timeline ended or not.
     */
    setIsProjectEnded: (
      state: ResourcePlanState,
      action: PayloadAction<boolean>
    ): void => {
      state.isProjectEnded = action.payload;
    },

    /**
     * Sets the disable state of the table to the new state.
     *
     * @param {ResourcePlanState} state - The resource plan state with all values.
     * @param {PayloadAction<boolean>} action - The action containing the new disable state.
     */
    setIsTableDisabled: (
      state: ResourcePlanState,
      action: PayloadAction<boolean>
    ): void => {
      state.resourcePlanTableState.isTableDisabled = action.payload;
    },
    /**
     * Sets the total draft requests to the new state.
     *
     * @param {ResourcePlanState} state - The resource plan state with all values.
     * @param {PayloadAction<number>} totalDraftRequests - The total draft requests to be set.
     */
    setTotalDraftRequests: (
      state: ResourcePlanState,
      totalDraftRequests: PayloadAction<number>
    ): void => {
      state.totalDraftRequests = totalDraftRequests.payload;
    },

    /**
     * Sets the project id state to the new state.
     *
     * @param {ResourcePlanState} state - The request overview filter state with all values.
     * @param {PayloadAction<string>} projectId - The project id string.
     */
    setProjectId: (
      state: ResourcePlanState,
      projectId: PayloadAction<string>
    ): void => {
      state.projectId = projectId.payload;
    },

    /**
     * Sets the isJointVentureProject state to the new state.
     * @param state - The resource plan filter state with all values.
     * @param project - The project object.
     */
    setIsJointVentureProject: (
      state: ResourcePlanState,
      project: PayloadAction<Project>
    ): void => {
      state.isJointVentureProject = !!project?.payload?.jointVenturePartner;
      state.jointVenturePartner =
        project?.payload?.jointVenturePartner ?? undefined;
    },

    /**
     * Sets the quick filter state to the new state.
     *
     * @param {ResourcePlanState} state - The resource plan filter state with all values.
     * @param {PayloadAction<string>} quickSearch - The quick search string.
     */
    setQuickFilter: (
      state: ResourcePlanState,
      quickSearch: PayloadAction<string>
    ): void => {
      state.resourcePlanTableState.quickFilter = quickSearch.payload;
      state.resourcePlanTableState.isFilterApplied =
        checkIfFilterApplied(state);
    },

    /**
     * Sets the custom table filter state to the new state.
     *
     * @param {ResourcePlanState} state - The resource plan filter state with all values.
     * @param {PayloadAction<ResourcePlanFilters>} customTableFilters - The custom table filters object with all filters.
     */
    setCustomTableFilter: (
      state: ResourcePlanState,
      customTableFilters: PayloadAction<ResourcePlanFilters>
    ): void => {
      state.resourcePlanTableState.customTableFilters =
        customTableFilters.payload;
      state.resourcePlanTableState.isFilterApplied =
        checkIfFilterApplied(state);
    },

    /**
     * Sets the active tab to the new state.
     *
     * @param state - The resource plan filter state with all values.
     * @param activeTab - The active tab to be set.
     */
    setActiveTab: (
      state: ResourcePlanState,
      activeTab: PayloadAction<string>
    ): void => {
      state.resourcePlanTableState.currentTabConfig.key = activeTab.payload;
    },

    /**
     * Sets the columns to the new state.
     *
     * @param {ResourcePlanState} state - The resource plan filter state with all values.
     * @param {PayloadAction<string[]>} columns - The columns to be set.
     */
    setColumns: (
      state: ResourcePlanState,
      columns: PayloadAction<string[]>
    ): void => {
      state.resourcePlanTableState.columns = columns.payload;
    },

    /**
     * Sets the current tab config to the new state.
     *
     * @param {ResourcePlanState} state - The resource plan filter state with all values.
     * @param {PayloadAction<TabItem>} tabConfig - The config for the Tab
     */
    setCurrentTabConfig: (
      state: ResourcePlanState,
      tabConfig: PayloadAction<TabItem>
    ): void => {
      state.resourcePlanTableState.currentTabConfig = tabConfig.payload;
    },

    /**
     * Clears a specific property from the custom table filter.
     *
     * @param {ResourcePlanState} state - The resource plan filter state with all values.
     * @param {Object} action - Contains the key and value to be cleared.
     * @returns {ResourcePlanState} - Updated state with the specified filter value removed.
     */
    clearCustomTableFilterProperty: (
      state: ResourcePlanState,
      action: { payload: { key: string; value: string } }
    ): ResourcePlanState => {
      const { key, value } = action.payload;
      return {
        ...state,
        resourcePlanTableState: {
          ...state.resourcePlanTableState,
          customTableFilters: clearCustomTableFilters(
            state.resourcePlanTableState.customTableFilters,
            key,
            value
          ),
        },
      };
    },

    /**
     * Clears the filter state to the initial state.
     *
     * @param {ResourcePlanState} state - The resource plan filter state with all values.
     * @returns {ResourcePlanState} - Updated state with the specified filter value removed.
     */
    clearFilter: (state: ResourcePlanState): ResourcePlanState => {
      return {
        ...state,
        resourcePlanTableState: {
          ...initialState.resourcePlanTableState,
          columns: state.resourcePlanTableState.columns, // preserve the columns
          quickFilter: state.resourcePlanTableState.quickFilter, // preserve the quick filter
          currentTabConfig: state.resourcePlanTableState.currentTabConfig, // preserve the current tab config
        },
      };
    },

    /**
     * Clears the filter state but preserves the project id.
     *
     * @param {ResourcePlanState} state - The resource plan filter state with all values.
     * @returns {ResourcePlanState} - Updated state with the specified filter value removed.
     */
    clearFilterSaveProjectId: (state: ResourcePlanState): ResourcePlanState => {
      return {
        ...initialState,
        projectId: state.projectId, // preserve the project id
        totalDraftRequests: state.totalDraftRequests, // preserve the total draft requests
        resourcePlanTableState: {
          ...initialState.resourcePlanTableState,
          columns: state.resourcePlanTableState.columns,
          currentTabConfig: state.resourcePlanTableState.currentTabConfig, // preserve the current tab config
        },
      };
    },
  },
});

export const {
  setTotalDraftRequests,
  setProjectId,
  setIsJointVentureProject,
  setQuickFilter,
  setCustomTableFilter,
  setColumns,
  setCurrentTabConfig,
  clearCustomTableFilterProperty,
  clearFilter,
  clearFilterSaveProjectId,
  setIsTableDisabled,
  setIsProjectEnded,
} = resourcePlanSlice.actions;

export default resourcePlanSlice;
